/* eslint-disable */

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import onResize from "simple-element-resize-detector";
import MapAdapter from "./MapAdapter";
import {
  OSRM_BASE_URL,
  OSM_TILE_URL,
  OSM_ATTRIBUTION,
} from "../config/mapProvider";

// Implementação Leaflet + OpenStreetMap (tiles grátis, sem API key) +
// roteamento OSRM (open-source, grátis). Não depende de nenhuma chave.
export default class LeafletMapAdapter extends MapAdapter {
  constructor() {
    super();
    this.map = null;
    this.markers = new Map(); // key -> L.Marker
    this.routeLayers = []; // L.Polyline da rota desenhada
    this._onViewChange = null;
  }

  init({ container, center, zoom, onViewChange }) {
    this._onViewChange = onViewChange;

    this.map = L.map(container, {
      center: [center.lat, center.lng],
      zoom,
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer(OSM_TILE_URL, {
      attribution: OSM_ATTRIBUTION,
      maxZoom: 19,
    }).addTo(this.map);

    const reportView = () => {
      if (!this._onViewChange) return;
      const c = this.map.getCenter();
      this._onViewChange(this.map.getZoom(), c.lat, c.lng);
    };
    this.map.on("moveend", reportView);
    this.map.on("zoomend", reportView);

    onResize(container, () => {
      this.map.invalidateSize();
    });

    // Garante o cálculo correto do tamanho depois que o container fica visível.
    setTimeout(() => {
      if (this.map) this.map.invalidateSize();
    }, 0);
  }

  resize() {
    if (this.map) this.map.invalidateSize();
  }

  setView(lat, lng, zoom) {
    if (!this.map) return;
    this.map.setView([lat, lng], zoom || this.map.getZoom());
  }

  destroy() {
    // Mantém o mapa vivo (a store guarda a referência); apenas timers/listeners
    // próprios seriam limpos aqui. Nada a fazer no momento.
  }

  get raw() {
    return this.map;
  }

  // ---- marcadores ----

  _buildIcon(svg, opts = {}) {
    const size = opts.size || [46, 46];
    // Âncora padrão: ponta inferior central (pin). Sobrescrevível via opts.anchor.
    const anchor = opts.anchor || [size[0] / 2, size[1]];
    return L.divIcon({
      html: svg,
      className: "sagres-divicon", // evita o quadrado branco padrão do Leaflet
      iconSize: size,
      iconAnchor: anchor,
    });
  }

  addMarker(key, lat, lng, svg, opts = {}) {
    const marker = L.marker([lat, lng], {
      icon: this._buildIcon(svg, opts),
      zIndexOffset: opts.zIndex || 0,
      keyboard: false,
    });
    if (opts.onTap) {
      marker.on("click", () => opts.onTap());
    }
    marker.addTo(this.map);
    this.markers.set(key, marker);
    return marker;
  }

  hasMarker(key) {
    return this.markers.has(key);
  }

  getMarkerPosition(key) {
    const marker = this.markers.get(key);
    if (!marker) return null;
    const ll = marker.getLatLng();
    return { lat: ll.lat, lng: ll.lng };
  }

  setMarkerPosition(key, lat, lng) {
    const marker = this.markers.get(key);
    if (marker) marker.setLatLng([lat, lng]);
  }

  setMarkerIcon(key, svg, opts = {}) {
    const marker = this.markers.get(key);
    if (marker) marker.setIcon(this._buildIcon(svg, opts));
  }

  removeMarker(key) {
    const marker = this.markers.get(key);
    if (marker) {
      this.map.removeLayer(marker);
      this.markers.delete(key);
    }
  }

  getMarkerKeys() {
    return Array.from(this.markers.keys());
  }

  // ---- rotas (OSRM) ----

  // Converte "lat,lng" -> "lng,lat" (formato exigido pelo OSRM).
  _toLonLat(latLngStr) {
    const [lat, lng] = latLngStr.split(",");
    return `${lng.trim()},${lat.trim()}`;
  }

  showRoute({ origin, destination, via }) {
    const points = [origin, ...via, destination].map((p) =>
      this._toLonLat(p)
    );
    const coords = points.join(";");
    const url = `${OSRM_BASE_URL}/route/v1/driving/${coords}?overview=full&geometries=geojson`;

    return fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (!data.routes || !data.routes.length) {
          console.log("OSRM: nenhuma rota encontrada", data.code);
          return;
        }
        this.clearRoutes();

        // GeoJSON vem em [lng, lat]; Leaflet quer [lat, lng].
        const latlngs = data.routes[0].geometry.coordinates.map(
          ([lng, lat]) => [lat, lng]
        );

        // Contorno + linha tracejada branca por cima (visual de seta/rota).
        const outline = L.polyline(latlngs, {
          color: "rgba(0, 128, 255, 0.7)",
          weight: 10,
          lineCap: "round",
        }).addTo(this.map);
        const dashes = L.polyline(latlngs, {
          color: "#ffffff",
          weight: 4,
          opacity: 0.9,
          dashArray: "1 14",
          lineCap: "round",
        }).addTo(this.map);

        this.routeLayers.push(outline, dashes);
      })
      .catch((err) => {
        console.log("Erro ao calcular rota OSRM:", err.message);
      });
  }

  clearRoutes() {
    this.routeLayers.forEach((layer) => this.map.removeLayer(layer));
    this.routeLayers = [];
  }

  // ---- geo ----

  distanceMeters(a, b) {
    return L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng));
  }
}
